import { useMemo } from 'react';
import { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import type { MultiValue, SingleValue } from 'react-select';

import {
  ColorsV2,
  Field,
  FieldLabel,
  SearchAsyncSelect,
  useThemeColors,
} from '@electricjs/arc';

import { Group } from '@/types/groups';

// Stable empty-array default so callers that omit `lockedGroups` don't cause
// a fresh array reference (and downstream recomputation) on every render.
const NO_LOCKED_GROUPS: Group[] = [];

type EmployeeGroupSelectorProps = {
  // Since this component is used in a form, we need to pass in the setValue and watch functions from react-hook-form
  // with <any> to account for the various form properties that may be set in the parent form args
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue: UseFormSetValue<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  watch: UseFormWatch<any>;
  label: string;
  required?: boolean;
  placeholder?: string;
  groups: Group[];
  disabled?: boolean;
  helperText?: string;
  // Groups managed outside this component (e.g. by a sibling read-only display).
  // Never shown as part of this component's own value, and always preserved
  // when writing back to the form's `groups` field.
  lockedGroups?: Group[];
};

const EmployeeGroupSelector = ({
  label,
  required,
  placeholder,
  setValue,
  watch,
  groups,
  disabled,
  helperText,
  lockedGroups = NO_LOCKED_GROUPS,
}: EmployeeGroupSelectorProps) => {
  const watchedGroups = watch('groups');
  const visibleValue = useMemo(() => {
    const lockedGroupIds = new Set(lockedGroups.map(group => group.id));
    return (watchedGroups ?? []).filter(
      (group: Group) => !lockedGroupIds.has(group.id)
    );
  }, [watchedGroups, lockedGroups]);

  const handleGroupChange = (
    newValue: SingleValue<Group> | MultiValue<Group>
  ) => {
    if (Array.isArray(newValue)) {
      setValue('groups', [...Array.from(newValue), ...lockedGroups], {
        shouldTouch: true,
        shouldDirty: true,
      });
    } else {
      setValue('groups', [newValue, ...lockedGroups], {
        shouldTouch: true,
        shouldDirty: true,
      });
    }
  };

  const requiredColor = useThemeColors(ColorsV2.ERROR) as string;

  return (
    <Field data-testid="employee-group-selector">
      <FieldLabel>
        {label}
        {required && <span style={{ color: requiredColor }}>&nbsp;*</span>}
      </FieldLabel>
      {helperText && (
        <FieldLabel variant="legal" pb={'1rem'}>
          {helperText}
        </FieldLabel>
      )}
      <SearchAsyncSelect<Group>
        id="employee-group-search-selector"
        // NOTE: SearchAsyncSelect in Arc only supports single-select (extends SelectProps),
        // but react-select's AsyncSelect supports multi-select. We use typed onChange handler
        // (MultiValue<Group>) for type safety while using @ts-expect-error for the Arc type mismatch.
        // @ts-expect-error - isMulti not in Arc's SearchAsyncSelectProps (single-select only)
        isMulti
        isClearable={false}
        isSearchable
        placeholder={placeholder}
        value={visibleValue}
        name="groups"
        maxMenuHeight={200}
        onChange={handleGroupChange}
        getOptionValue={group => group.id}
        getOptionLabel={group => group.name}
        fetchOptions={async (searchTerm: string) => {
          // async keyword implicitly returns a promise, so no need to explicitly return a promise
          return searchTerm
            ? groups.filter(group =>
                group.name.toLowerCase().includes(searchTerm.toLowerCase())
              )
            : groups;
        }}
        defaultOptions={groups}
        disabled={disabled}
      />
    </Field>
  );
};

export default EmployeeGroupSelector;
